import React, { useState, useEffect, useRef } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const isKorean = true;
const t = isKorean ? ko : en;

/* ─── Sticker Types & Constants ─────────────────────────────────────────── */
const STICKER_BASE_SIZE = 90;

type PlacedSticker = {
  id: string;
  itemId: number;
  imageUrl: string;  // 실제 렌더링에 사용할 이미지 URL
  label: string;
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

// 서버 API에서 가져온 스티커 아이템
type StickerLibraryItem = {
  itemId: number;
  imageUrl: string;
  label: string;
  category: string;
};

// 서버 item name → 로컬 logo_image 파일 fallback 매핑
const NAME_TO_LOCAL: Record<string, string> = {
  'bird_mascot': 'bird_mascot.png',
  'mascot':      'bird_mascot.png',
  'dojang':      'dojang.png',
  'stamp':       'dojang.png',
  'fly':         'fly1.png',
  'fly1':        'fly1.png',
  'letter':      'letter2.png',
  'letter2':     'letter2.png',
  'logo':        'logo.png',
  'sea':         'sea.png',
  'ocean':       'sea.png',
  'up':          'up.png',
  'world':       'world_map.png',
  'world_map':   'world_map.png',
  'map':         'world_map.png',
};

const getLocalFallback = (name: string): string => {
  const key = name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  for (const [k, v] of Object.entries(NAME_TO_LOCAL)) {
    if (key.includes(k)) return `${process.env.PUBLIC_URL}/logo_image/${v}`;
  }
  return `${process.env.PUBLIC_URL}/logo_image/bird_mascot.png`;
};

// API 실패 시 사용할 로컇 fallback 스티커 목록
const getFallbackLibrary = (): StickerLibraryItem[] => [
  { itemId: -1, imageUrl: `${process.env.PUBLIC_URL}/logo_image/bird_mascot.png`, label: '마스코트', category: '동물' },
  { itemId: -2, imageUrl: `${process.env.PUBLIC_URL}/logo_image/dojang.png`,      label: '도장', category: '기호' },
  { itemId: -3, imageUrl: `${process.env.PUBLIC_URL}/logo_image/fly1.png`,        label: '날개', category: '기호' },
  { itemId: -4, imageUrl: `${process.env.PUBLIC_URL}/logo_image/letter2.png`,     label: '편지', category: '사물' },
  { itemId: -5, imageUrl: `${process.env.PUBLIC_URL}/logo_image/logo.png`,        label: '로고', category: '기호' },
  { itemId: -6, imageUrl: `${process.env.PUBLIC_URL}/logo_image/sea.png`,         label: '바다', category: '자연' },
  { itemId: -7, imageUrl: `${process.env.PUBLIC_URL}/logo_image/up.png`,          label: 'UP', category: '기호' },
  { itemId: -8, imageUrl: `${process.env.PUBLIC_URL}/logo_image/world_map.png`,   label: '세계지도', category: '사물' },
];

type DragAction =
  | { type: 'move';   stickerId: string; startClientX: number; startClientY: number; startX: number; startY: number }
  | { type: 'resize'; stickerId: string; centerX: number; centerY: number; startDist: number; startScale: number }
  | { type: 'rotate'; stickerId: string; centerX: number; centerY: number; startAngle: number; startRotation: number };

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to   { transform: translateY(0);    opacity: 1; }
`;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #D8D0B8 transparent;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: #D8D0B8; border-radius: 3px; }
`;

const WriteContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px;
  gap: 20px;
  position: relative;
`;

const TopRightMascot = styled.img`
  position: absolute;
  top: -10px;
  right: 12px;
  width: 80px;
  height: auto;
  z-index: 10;
  transform: rotate(15deg);
  pointer-events: none;
`;

const RowContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  @media (max-width: 420px) { flex-direction: column; }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const InputLabel = styled.label`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #5C5040;
`;

const StyledInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 24px;
  background: #FFF;
  padding: 12px 18px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { border-color: #E07040; box-shadow: 0 0 0 3px rgba(224,112,64,0.1); }
  &::placeholder { color: #C8C0A8; }
`;

const SpeedControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div<{ $isOpen: boolean }>`
  width: 100%;
  border: 1.5px solid ${p => p.$isOpen ? '#E07040' : '#D8D0B8'};
  border-radius: 24px;
  background: #FFF;
  padding: 12px 18px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: ${p => p.$isOpen ? '0 0 0 3px rgba(224,112,64,0.1)' : 'none'};
  &:hover { border-color: #E07040; }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #E07040;
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: #FFF;
  border: 1.5px solid #D8D0B8;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(90,60,30,0.1);
  overflow: hidden;
  z-index: 50;
  display: flex;
  flex-direction: column;
`;

const DropdownItem = styled.div<{ $active?: boolean }>`
  padding: 12px 18px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.$active ? '#E07040' : '#5C5040'};
  background: ${p => p.$active ? '#F9F6E8' : 'transparent'};
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #F9F6E8; }
  &:not(:last-child) { border-bottom: 1px solid #F0ECE0; }
`;

/* ─── Letter Paper ───────────────────────────────────────────────────────── */
const LinedTextContainer = styled.div`
  flex: 1;
  border: 1.5px solid #D8D0B8;
  border-radius: 24px;
  background: #FFF;
  overflow: visible; /* allow sticker handles to appear outside bounds */
  position: relative;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  margin-bottom: 80px;
`;

const LetterTextArea = styled.textarea`
  width: 100%;
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #5C5040;
  line-height: 40px;
  padding: 10px 20px;
  position: relative;
  z-index: 1;
  border-radius: 24px;
  background-image: repeating-linear-gradient(
    transparent,
    transparent 39px,
    #EDE9D5 39px,
    #EDE9D5 40px
  );
  background-attachment: local;
  &::placeholder { color: #C8C0A8; }
`;

/* ─── Buttons ────────────────────────────────────────────────────────────── */
const BottomSendBtn = styled.button`
  position: absolute;
  bottom: 24px;
  right: 24px;
  background: #E07040;
  color: #fff;
  border: none;
  border-radius: 100px;
  padding: 14px 28px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(224,112,64,0.3);
  transition: transform 0.15s, background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 20;
  &:hover { background: #C85E30; transform: translateY(-2px); }
  &:active { transform: translateY(1px); }
  &:disabled { background: #C8C0A8; box-shadow: none; cursor: not-allowed; transform: none; }
`;

/* ─── Modal ──────────────────────────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;
const ModalContent = styled.div`
  background: #F9F6E8;
  padding: 24px;
  border-radius: 20px;
  width: 80%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.2);
`;
const ModalTitle = styled.h3`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #E07040;
  text-align: center;
`;
const ModalDesc = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  color: #5C5040;
  text-align: center;
  line-height: 1.4;
  word-break: keep-all;
`;
const ModalBtn = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: none;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: #E07040;
  color: #FFF;
  margin-top: 8px;
  transition: background 0.2s;
  &:hover { background: #C85E30; }
`;

/* ════════════════════════════════════════════════════════════════════════════
   STICKER STYLED COMPONENTS
   ════════════════════════════════════════════════════════════════════════════ */

/* Sticker toggle button – bottom left */
const StickerToggleBtn = styled.button<{ $active: boolean }>`
  position: absolute;
  bottom: 24px;
  left: 24px;
  width: 52px;
  height: 52px;
  background: ${p => p.$active ? '#E07040' : '#F0EBD8'};
  border: 1.5px solid ${p => p.$active ? '#C85E30' : '#D8D0B8'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  font-size: 22px;
  color: ${p => p.$active ? '#FFF' : '#E07040'};
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(90,60,30,0.12);
  &:hover { background: ${p => p.$active ? '#C85E30' : '#E8E0C8'}; transform: translateY(-2px); box-shadow: 0 4px 14px rgba(90,60,30,0.18); }
  &:active { transform: scale(0.94); }
`;

/* Sticker panel overlay */
const StickerPanelOverlay = styled.div<{ $show: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 80;
  pointer-events: ${p => p.$show ? 'auto' : 'none'};
`;
const StickerPanelBackdrop = styled.div<{ $show: boolean }>`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.3);
  opacity: ${p => p.$show ? 1 : 0};
  transition: opacity 0.3s;
`;
const StickerSheet = styled.div<{ $show: boolean }>`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background: #FDFBF4;
  border-radius: 24px 24px 0 0;
  padding: 12px 20px 32px;
  transform: translateY(${p => p.$show ? '0' : '110%'});
  transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  max-height: 58%;
  overflow-y: auto;
  box-shadow: 0 -8px 32px rgba(0,0,0,0.18);
`;
const StickerSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #D8D0B8;
  border-radius: 2px;
  margin: 0 auto 14px;
`;
const StickerSheetTitle = styled.div`
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #5C5040;
  margin-bottom: 14px;
  text-align: center;
`;
const StickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
`;
const StickerThumbWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;
const StickerThumb = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 16px;
  background: #F5F2E8;
  border: 1.5px solid #EDE9D5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  overflow: hidden;
  padding: 8px;
  &:hover { transform: scale(1.07); box-shadow: 0 4px 12px rgba(90,60,30,0.18); border-color: #E07040; }
  &:active { transform: scale(0.95); }
  img { width: 100%; height: 100%; object-fit: contain; }
`;
const StickerThumbLabel = styled.div`
  font-family: 'Jua', sans-serif;
  font-size: 10px;
  color: #A09070;
  text-align: center;
`;

/* Sticker canvas (overlaid on the textarea) */
const StickerCanvas = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  overflow: visible;
  border-radius: 24px;
`;

/* Mobile sticker control bar */
const MobileStickerBar = styled.div<{ $show: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 12px 14px;
  background: rgba(249,246,232,0.97);
  border-top: 1.5px solid #EDE9D5;
  backdrop-filter: blur(8px);
  opacity: ${p => p.$show ? 1 : 0};
  transform: translateY(${p => p.$show ? '0' : '8px'});
  transition: opacity 0.2s, transform 0.2s;
  pointer-events: ${p => p.$show ? 'auto' : 'none'};
  max-height: ${p => p.$show ? '80px' : '0px'};
  overflow: hidden;
`;
const MobileCtrlBtn = styled.button<{ $danger?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 7px 10px;
  border: none;
  border-radius: 12px;
  background: ${p => p.$danger ? '#FFF0EE' : '#F5F2E8'};
  color: ${p => p.$danger ? '#D06060' : '#5C5040'};
  cursor: pointer;
  font-size: 18px;
  transition: background 0.15s, transform 0.1s;
  &:active { transform: scale(0.92); }
  span {
    font-size: 9px;
    font-family: 'Noto Sans KR', sans-serif;
    font-weight: 700;
    color: ${p => p.$danger ? '#D06060' : '#A09070'};
    white-space: nowrap;
  }
`;

/* PC transform handle (corner / rotate) */
const TransformHandle = styled.div<{ $htype: 'nw'|'ne'|'sw'|'se'|'rot'|'del' }>`
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  border: 2.5px solid #E07040;
  border-radius: ${p => p.$htype === 'rot' ? '50%' : '3px'};
  pointer-events: auto;
  z-index: 30;
  transition: background 0.15s;

  ${p => p.$htype === 'nw' && `top: -7px; left: -7px;   cursor: nw-resize;`}
  ${p => p.$htype === 'ne' && `top: -7px; right: -7px;  cursor: ne-resize;`}
  ${p => p.$htype === 'sw' && `bottom: -7px; left: -7px; cursor: sw-resize;`}
  ${p => p.$htype === 'se' && `bottom: -7px; right: -7px; cursor: se-resize;`}
  ${p => p.$htype === 'rot' && `
    top: -36px;
    left: 50%;
    transform: translateX(-50%);
    cursor: crosshair;
    background: #E07040;
    border-color: #fff;
    box-shadow: 0 2px 6px rgba(224,112,64,0.5);
  `}
  ${p => p.$htype === 'del' && `
    top: -36px;
    right: -7px;
    cursor: pointer;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #E87070;
    font-weight: bold;
    box-shadow: none;
  `}

  &:hover {
    background: ${p => {
      if (p.$htype === 'rot') return '#C85E30';
      if (p.$htype === 'del') return 'transparent';
      return '#F9F6E8';
    }};
    color: ${p => p.$htype === 'del' ? '#C04040' : 'inherit'};
    transform: ${p => p.$htype === 'del' ? 'scale(1.1)' : 'none'};
  }
`;

const RotHandleStem = styled.div`
  position: absolute;
  left: 50%;
  top: -22px;
  width: 2px;
  height: 18px;
  background: #E07040;
  transform: translateX(-50%);
  pointer-events: none;
`;

/* ─── Component ─────────────────────────────────────────────────────────── */
const Letter: React.FC = () => {
  /* ── existing state ─────────────────────────────────────────────────────── */
  const [sender, setSender]           = useState('');
  const [recipient, setRecipient]     = useState('');
  const [speed, setSpeed]             = useState('relaxed');
  const [customDays, setCustomDays]   = useState('');
  const [customHours, setCustomHours] = useState('');
  const [customMins, setCustomMins]   = useState('');
  const [isSpeedDropdownOpen, setIsSpeedDropdownOpen]       = useState(false);
  const [isRecipientDropdownOpen, setIsRecipientDropdownOpen] = useState(false);
  const [friends, setFriends]         = useState<{ user_email: string; user_name: string; user_alias?: string }[]>([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [content, setContent]         = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [modalState, setModalState]   = useState<{ type: 'success'|'error'; message: string } | null>(null);

  /* ── sticker state ──────────────────────────────────────────────────────── */
  const [placedStickers, setPlacedStickers]       = useState<PlacedSticker[]>([]);
  const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null);
  const [showStickerPanel, setShowStickerPanel]   = useState(false);
  const [stickerLibrary, setStickerLibrary]       = useState<StickerLibraryItem[]>([]);
  const [stickerLibLoading, setStickerLibLoading] = useState(false);
  const [selectedStickerCategory, setSelectedStickerCategory] = useState<string>('전체');

  // Detect hover/fine-pointer device (mouse vs touch-only)
  const [isHoverDevice] = useState(() =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );

  const stickerCanvasRef = useRef<HTMLDivElement>(null);
  const dragStateRef     = useRef<DragAction | null>(null);
  const wasDraggingRef   = useRef(false);

  /* ── fetch sticker library from server ────────────────────────────────── */
  useEffect(() => {
    const fetchItems = async () => {
      setStickerLibLoading(true);
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/item/list`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          const items: StickerLibraryItem[] = (Array.isArray(data) ? data : data.items || [])
            .filter((it: any) => it.item_type === 'STICKER' || !it.item_type)
            .map((it: any) => {
              // 상대경로 image_url에 서버 prefix 붙이기
              let imageUrl = it.image_url || '';
              if (imageUrl && !imageUrl.startsWith('http')) {
                imageUrl = `${imageUrl}`;
              }
              let category = it.category;
              if (!category) {
                if (it.name && it.name.includes('이모티콘')) category = '이모티콘';
                else if (it.item_type === 'STATIONERY') category = '편지지';
                else category = '기본';
              }
              return {
                itemId:   it.id,
                imageUrl: imageUrl || '',
                label:    it.name || `스티커 ${it.id}`,
                category: category
              };
            });
          if (items.length > 0) {
            setStickerLibrary(items);
          } else {
            // 서버 아이템이 없으면 로컬 이미지로 fallback (테스트용)
            setStickerLibrary(getFallbackLibrary());
          }
        } else {
          setStickerLibrary(getFallbackLibrary());
        }
      } catch {
        setStickerLibrary(getFallbackLibrary());
      } finally {
        setStickerLibLoading(false);
      }
    };
    fetchItems();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch friends ─────────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchFriends = async () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/following`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) { const d = await res.json(); setFriends(Array.isArray(d) ? d : []); }
      } catch {}
    };
    fetchFriends();
  }, []);

  /* ── global drag handlers ───────────────────────────────────────────────── */
  const handleGlobalMove = (clientX: number, clientY: number) => {
    const drag = dragStateRef.current;
    if (!drag || !stickerCanvasRef.current) return;
    const rect = stickerCanvasRef.current.getBoundingClientRect();

    setPlacedStickers(prev => prev.map(s => {
      if (s.id !== drag.stickerId) return s;
      if (drag.type === 'move') {
        const dx = ((clientX - drag.startClientX) / rect.width)  * 100;
        const dy = ((clientY - drag.startClientY) / rect.height) * 100;
        return { ...s, x: Math.max(2, Math.min(98, drag.startX + dx)), y: Math.max(2, Math.min(98, drag.startY + dy)) };
      }
      if (drag.type === 'resize') {
        const dx = clientX - drag.centerX;
        const dy = clientY - drag.centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return { ...s, scale: Math.max(0.2, Math.min(6, drag.startScale * dist / Math.max(drag.startDist, 1))) };
      }
      if (drag.type === 'rotate') {
        const angle = Math.atan2(clientY - drag.centerY, clientX - drag.centerX) * (180 / Math.PI);
        return { ...s, rotation: drag.startRotation + (angle - drag.startAngle) };
      }
      return s;
    }));
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handleGlobalMove(e.clientX, e.clientY);
    const onMouseUp   = () => {
      if (dragStateRef.current) {
        wasDraggingRef.current = true;
        setTimeout(() => { wasDraggingRef.current = false; }, 100);
      }
      dragStateRef.current = null;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!dragStateRef.current) return;
      e.preventDefault();
      handleGlobalMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd  = () => {
      if (dragStateRef.current) {
        wasDraggingRef.current = true;
        setTimeout(() => { wasDraggingRef.current = false; }, 100);
      }
      dragStateRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend',  onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend',  onTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── sticker helpers ────────────────────────────────────────────────────── */
  const addSticker = (item: StickerLibraryItem) => {
    let newX = 50;
    let newY = 50;

    if (stickerCanvasRef.current) {
      const rect = stickerCanvasRef.current.getBoundingClientRect();
      const viewportCenterY = window.innerHeight / 2;
      const viewportCenterX = window.innerWidth / 2;

      const relativeY = viewportCenterY - rect.top;
      const relativeX = viewportCenterX - rect.left;

      newY = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
      newX = Math.max(0, Math.min(100, (relativeX / rect.width) * 100));
    }

    const s: PlacedSticker = {
      id: `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      itemId:   item.itemId,
      imageUrl: item.imageUrl,
      label:    item.label,
      x: newX,
      y: newY,
      scale: 1,
      rotation: 0,
    };
    setPlacedStickers(prev => [...prev, s]);
    setSelectedStickerId(s.id);
    setShowStickerPanel(false);
  };

  const deleteSelectedSticker = (id: string) => {
    setPlacedStickers(prev => prev.filter(s => s.id !== id));
    setSelectedStickerId(null);
  };

  const adjustScale    = (id: string, delta: number) =>
    setPlacedStickers(prev => prev.map(s => s.id === id ? { ...s, scale: Math.max(0.2, Math.min(6, s.scale + delta)) } : s));

  const adjustRotation = (id: string, delta: number) =>
    setPlacedStickers(prev => prev.map(s => s.id === id ? { ...s, rotation: s.rotation + delta } : s));

  /* start move drag */
  const startMove = (clientX: number, clientY: number, sticker: PlacedSticker) => {
    dragStateRef.current = {
      type: 'move', stickerId: sticker.id,
      startClientX: clientX, startClientY: clientY,
      startX: sticker.x, startY: sticker.y,
    };
  };

  /* start resize drag */
  const startResize = (e: React.MouseEvent | React.TouchEvent, sticker: PlacedSticker) => {
    e.stopPropagation();
    if (!stickerCanvasRef.current) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const rect = stickerCanvasRef.current.getBoundingClientRect();
    const cx = rect.left + (sticker.x / 100) * rect.width;
    const cy = rect.top  + (sticker.y / 100) * rect.height;
    const dx = clientX - cx; const dy = clientY - cy;
    const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 10);
    dragStateRef.current = { type: 'resize', stickerId: sticker.id, centerX: cx, centerY: cy, startDist: dist, startScale: sticker.scale };
  };

  /* start rotate drag */
  const startRotate = (e: React.MouseEvent | React.TouchEvent, sticker: PlacedSticker) => {
    e.stopPropagation();
    if (!stickerCanvasRef.current) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const rect = stickerCanvasRef.current.getBoundingClientRect();
    const cx = rect.left + (sticker.x / 100) * rect.width;
    const cy = rect.top  + (sticker.y / 100) * rect.height;
    const angle = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI);
    dragStateRef.current = { type: 'rotate', stickerId: sticker.id, centerX: cx, centerY: cy, startAngle: angle, startRotation: sticker.rotation };
  };

  /* ── send logic ─────────────────────────────────────────────────────────── */
  const speedOptions = [
    { id: 'relaxed', label: t.write.speedOptions.relaxed,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { id: 'normal',  label: t.write.speedOptions.normal,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><polyline points="12 22 12 16 9 16"/><polyline points="12 16 15 16 15 22"/><line x1="8" y1="12" x2="16" y2="12"/></svg> },
    { id: 'fast',    label: t.write.speedOptions.fast,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l4-4a2.1 2.1 0 0 0-3-3l-4 4-8.2-1.8-2.6 2.6 6 3-3 3-3-1-2 2 4.5 4.5 2-2-1-3 3-3 3 6 2.6-2.6z"/></svg> },
    { id: 'custom',  label: t.write.speedOptions.custom,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg> },
  ];
  const selectedOption = speedOptions.find(o => o.id === speed) || speedOptions[0];
  const isFormValid    = sender.trim().length > 0 && recipient.trim().length > 0 && content.trim().length > 0;

  const handleSend = async () => {
    if (!isFormValid || isLoading) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const getRndMins = (minD: number, maxD: number) => {
        const mn = minD * 1440; const mx = maxD * 1440;
        return Math.floor(Math.random() * (mx - mn + 1)) + mn;
      };
      let mappedMins: number;
      if (speed === 'relaxed')    mappedMins = getRndMins(8, 10);
      else if (speed === 'normal') mappedMins = getRndMins(5, 6);
      else if (speed === 'fast')   mappedMins = getRndMins(2, 3);
      else {
        const total = (Number(customDays)||0)*1440 + (Number(customHours)||0)*60 + (Number(customMins)||0);
        mappedMins = Math.max(1, total);
      }
      const totalDays = mappedMins / 1440;
      let dest = 'North America';
      if      (totalDays <= 1) dest = 'South Korea';
      else if (totalDays <= 2) dest = 'Japan';
      else if (totalDays <= 4) dest = 'Southeast Asia';

      const payload: any = {
        receiver_alias: recipient,
        send_type: 'EMS',
        arrival_delay_minutes: mappedMins,
        title: content.substring(0, 20) + (content.length > 20 ? '...' : ''),
        content,
        origin: 'South Korea',
        destination: dest,
      };

      // 스티커 데이터 포함 (서버 실제 아이템만 전송, fallback itemId < 0 제외)
      const validStickers = placedStickers.filter(s => s.itemId > 0);
      if (validStickers.length > 0) {
        payload.stickers = validStickers.map(s => ({
          item_id:    s.itemId,
          x_position: Math.round(s.x * 10) / 10,
          y_position: Math.round(s.y * 10) / 10,
          z_position: Math.round(s.rotation * 10) / 10,
          scale:      Math.round(s.scale * 100) / 100,
          size:       Math.round(s.scale * 100) / 100, // in case backend uses 'size'
          rotation:   Math.round(s.rotation * 10) / 10,
          width:      Math.round(s.scale * 90 * 10) / 10,
          height:     Math.round(s.scale * 90 * 10) / 10,
        }));
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/mail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setModalState({ type: 'success', message: t.write.alertSuccess });
      } else {
        const errData = await res.json().catch(() => null);
        setModalState({ type: 'error', message: `${t.write.alertFail} (${JSON.stringify(errData)})` });
      }
    } catch {
      setModalState({ type: 'error', message: t.write.alertError });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSticker = placedStickers.find(s => s.id === selectedStickerId);

  /* ─── JSX ──────────────────────────────────────────────────────────────── */
  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>
        {/* Nav Bar */}
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/'; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </BackBtn>
          <NavTitle>{t.nav.write}</NavTitle>
          {/* 배치된 스티커 수 표시 */}
          {placedStickers.length > 0 && (
            <div style={{
              fontFamily: "'Jua', sans-serif", fontSize: '12px', color: '#E07040',
              background: '#FFF0E8', borderRadius: '20px', padding: '4px 10px',
            }}>
              🎨 ×{placedStickers.length}
            </div>
          )}
        </PhoneNavBar>

        {/* Body */}
        <PhoneBody>
          <WriteContainer onClick={() => {
            if (wasDraggingRef.current) return;
            setSelectedStickerId(null);
          }}>
            <TopRightMascot src={`${process.env.PUBLIC_URL}/logo_image/fly1.png`} alt="mascot" />

            {/* Sender / Recipient */}
            <RowContainer>
              <InputGroup>
                <InputLabel>{t.write.senderLabel}</InputLabel>
                <StyledInput
                  placeholder={t.write.senderPlaceholder}
                  value={sender}
                  onChange={e => setSender(e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel>{t.write.recipientLabel}</InputLabel>
                <DropdownContainer>
                  <DropdownHeader $isOpen={isRecipientDropdownOpen} onClick={() => setIsRecipientDropdownOpen(true)}>
                    <input
                      style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontFamily: "'Noto Sans KR', sans-serif", fontSize: '15px', color: '#3D3020' }}
                      placeholder={t.write.searchFriendPlaceholder}
                      value={recipientSearch}
                      onChange={e => { setRecipientSearch(e.target.value); setRecipient(''); setIsRecipientDropdownOpen(true); }}
                      onFocus={() => setIsRecipientDropdownOpen(true)}
                    />
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C8C0A8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: isRecipientDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); setIsRecipientDropdownOpen(v => !v); }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </DropdownHeader>
                  {isRecipientDropdownOpen && (
                    <DropdownList>
                      {(() => {
                        const filtered = friends.filter(f =>
                          f.user_name.toLowerCase().includes(recipientSearch.toLowerCase()) ||
                          (f.user_alias && f.user_alias.toLowerCase().includes(recipientSearch.toLowerCase()))
                        );
                        if (friends.length === 0) return <DropdownItem style={{ color: '#A09070', pointerEvents: 'none', justifyContent: 'center' }}>{t.write.noFollowedFriends}</DropdownItem>;
                        if (filtered.length === 0) return <DropdownItem style={{ color: '#A09070', pointerEvents: 'none', justifyContent: 'center' }}>{t.write.noSearchResults}</DropdownItem>;
                        return filtered.map(fr => (
                          <DropdownItem key={fr.user_alias || fr.user_email} $active={recipient === (fr.user_alias || fr.user_email)}
                            onClick={() => { setRecipient(fr.user_alias || fr.user_email); setRecipientSearch(fr.user_name); setIsRecipientDropdownOpen(false); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            {fr.user_name} <span style={{ fontSize: '12px', color: '#B0A080', marginLeft: '4px' }}>{fr.user_alias}</span>
                          </DropdownItem>
                        ));
                      })()}
                    </DropdownList>
                  )}
                </DropdownContainer>
              </InputGroup>
            </RowContainer>

            {/* Speed */}
            <SpeedControlGroup>
              <InputLabel>{t.write.speedLabel}</InputLabel>
              <DropdownContainer>
                <DropdownHeader $isOpen={isSpeedDropdownOpen} onClick={() => setIsSpeedDropdownOpen(v => !v)}>
                  <HeaderContent>{selectedOption.icon}{selectedOption.label}</HeaderContent>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C8C0A8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: isSpeedDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </DropdownHeader>
                {isSpeedDropdownOpen && (
                  <DropdownList>
                    {speedOptions.map(o => (
                      <DropdownItem key={o.id} $active={speed === o.id} onClick={() => { setSpeed(o.id); setIsSpeedDropdownOpen(false); }}>
                        {o.icon}{o.label}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </DropdownContainer>
              {speed === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  {[
                    { val: customDays,  set: setCustomDays,  label: t.write.days },
                    { val: customHours, set: setCustomHours, label: t.write.hours },
                    { val: customMins,  set: setCustomMins,  label: t.write.minutes },
                  ].map(({ val, set, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                      <StyledInput type="number" placeholder="0" value={val} onChange={e => set(e.target.value)}
                        style={{ textAlign: 'right', padding: '12px 14px' }} min="0" />
                      <span style={{ fontSize: '14px', color: '#5C5040', whiteSpace: 'nowrap' }}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </SpeedControlGroup>

            {/* ── Letter Paper with Sticker Canvas ── */}
            <LinedTextContainer>
              <LetterTextArea
                placeholder={t.write.contentPlaceholder}
                value={content}
                onChange={e => setContent(e.target.value)}
                spellCheck={false}
              />

              {/* Sticker Canvas Overlay */}
              <StickerCanvas ref={stickerCanvasRef}>
                {placedStickers.map(sticker => {
                  const isSelected = selectedStickerId === sticker.id;
                  const displaySize = STICKER_BASE_SIZE * sticker.scale;

                  return (
                    <div
                      key={sticker.id}
                      style={{
                        position: 'absolute',
                        left: `${sticker.x}%`,
                        top: `${sticker.y}%`,
                        width: `${displaySize}px`,
                        height: `${displaySize}px`,
                        transform: `translate(-50%, -50%) rotate(${sticker.rotation}deg)`,
                        transformOrigin: 'center center',
                        pointerEvents: 'auto',
                        cursor: 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                        zIndex: isSelected ? 100 : 6,
                      }}
                      onMouseDown={e => {
                        e.stopPropagation();
                        setSelectedStickerId(sticker.id);
                        startMove(e.clientX, e.clientY, sticker);
                      }}
                      onTouchStart={e => {
                        e.stopPropagation();
                        setSelectedStickerId(sticker.id);
                        startMove(e.touches[0].clientX, e.touches[0].clientY, sticker);
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {/* Sticker image */}
                      <img
                        src={sticker.imageUrl}
                        alt={sticker.label}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', display: 'block' }}
                        draggable={false}
                      />

                      {/* Selection dashed border */}
                      {isSelected && (
                        <div style={{
                          position: 'absolute', inset: '-6px',
                          border: '2px dashed #E07040', borderRadius: '8px',
                          pointerEvents: 'none',
                        }} />
                      )}

                      {/* ── Handles (PC & Mobile) ── */}
                      {isSelected && (
                        <>
                          {/* Rotation stem */}
                          <RotHandleStem />
                          {/* Rotation handle */}
                          <TransformHandle
                            $htype="rot"
                            onMouseDown={e => startRotate(e, sticker)}
                            onTouchStart={e => startRotate(e, sticker)}
                            title="드래그하여 회전"
                          />
                          {/* Delete handle */}
                          <TransformHandle
                            $htype="del"
                            onClick={e => { e.stopPropagation(); deleteSelectedSticker(sticker.id); }}
                            onTouchStart={e => { e.stopPropagation(); deleteSelectedSticker(sticker.id); }}
                            title="삭제"
                          >
                            ✕
                          </TransformHandle>
                          {/* Corner resize handles */}
                          {(['nw','ne','sw','se'] as const).map(h => (
                            <TransformHandle 
                              key={h} 
                              $htype={h} 
                              onMouseDown={e => startResize(e, sticker)} 
                              onTouchStart={e => startResize(e, sticker)} 
                            />
                          ))}
                        </>
                      )}
                    </div>
                  );
                })}
              </StickerCanvas>
            </LinedTextContainer>

            <StickerToggleBtn
              $active={showStickerPanel}
              onClick={e => { e.stopPropagation(); setShowStickerPanel(v => !v); setSelectedStickerId(null); }}
              title="스티커 추가"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                <path d="M16.4472 13.1056C16.9412 13.3526 17.1414 13.9532 16.8944 14.4472L16 14C16.8944 14.4472 16.8946 14.4468 16.8944 14.4472L16.8928 14.4504L16.8908 14.4543L16.8857 14.4644L16.8706 14.493C16.8585 14.5158 16.8421 14.5458 16.8214 14.5821C16.78 14.6544 16.7212 14.7523 16.6445 14.8672C16.4924 15.0954 16.2643 15.3999 15.9571 15.7071C15.3481 16.3161 14.3588 17 13 17C11.8272 17 10.9251 16.7056 10.3028 16.3944C9.99328 16.2397 9.75496 16.0818 9.5875 15.9563C9.5037 15.8934 9.43735 15.8384 9.38837 15.7955C9.36387 15.7741 9.34367 15.7557 9.32778 15.7408L9.30713 15.7211L9.29922 15.7134L9.29585 15.7101L9.29432 15.7085L9.2936 15.7078C9.29324 15.7075 9.2929 15.7071 9.99207 15.0079L9.2936 15.7078C8.90307 15.3173 8.90237 14.6834 9.29289 14.2929C9.68007 13.9057 10.3057 13.9024 10.697 14.2829L10.7054 14.2904C10.7189 14.3022 10.7463 14.3253 10.7875 14.3562C10.87 14.4182 11.0067 14.5103 11.1972 14.6056C11.5749 14.7944 12.1728 15 13 15C13.6412 15 14.1519 14.6839 14.5429 14.2929C14.7357 14.1001 14.8825 13.9046 14.9804 13.7578C15.0288 13.6852 15.0637 13.6268 15.0849 13.5898C15.0954 13.5713 15.1024 13.5584 15.1059 13.5519L15.1081 13.5478C15.3566 13.0575 15.9549 12.8594 16.4472 13.1056Z" fill="currentColor"/>
                <path d="M14 9C13.4477 9 13 9.44771 13 10C13 10.5523 13.4477 11 14 11H16C16.5523 11 17 10.5523 17 10C17 9.44771 16.5523 9 16 9H14Z" fill="currentColor"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23ZM12 20.9932C7.03321 20.9932 3.00683 16.9668 3.00683 12C3.00683 7.03321 7.03321 3.00683 12 3.00683C16.9668 3.00683 20.9932 7.03321 20.9932 12C20.9932 16.9668 16.9668 20.9932 12 20.9932Z" fill="currentColor"/>
              </svg>
            </StickerToggleBtn>

            {/* Send button */}
            <BottomSendBtn disabled={!isFormValid || isLoading} onClick={handleSend}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="6" r="3"/>
                <path d="M12 9v5"/><path d="M10 14h4a3 3 0 0 1 3 3v5H7v-5a3 3 0 0 1 3-3z"/>
                <path d="M5 22h14"/>
              </svg>
              {isLoading ? '전송 중…' : t.write.submitBtn}
            </BottomSendBtn>
          </WriteContainer>
        </PhoneBody>

        {/* ── Sticker Panel (slide-up sheet) ── */}
        <StickerPanelOverlay $show={showStickerPanel}>
          <StickerPanelBackdrop $show={showStickerPanel} onClick={() => setShowStickerPanel(false)} />
          <StickerSheet $show={showStickerPanel}>
            <StickerSheetHandle />
            <StickerSheetTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                <path d="M16.4472 13.1056C16.9412 13.3526 17.1414 13.9532 16.8944 14.4472L16 14C16.8944 14.4472 16.8946 14.4468 16.8944 14.4472L16.8928 14.4504L16.8908 14.4543L16.8857 14.4644L16.8706 14.493C16.8585 14.5158 16.8421 14.5458 16.8214 14.5821C16.78 14.6544 16.7212 14.7523 16.6445 14.8672C16.4924 15.0954 16.2643 15.3999 15.9571 15.7071C15.3481 16.3161 14.3588 17 13 17C11.8272 17 10.9251 16.7056 10.3028 16.3944C9.99328 16.2397 9.75496 16.0818 9.5875 15.9563C9.5037 15.8934 9.43735 15.8384 9.38837 15.7955C9.36387 15.7741 9.34367 15.7557 9.32778 15.7408L9.30713 15.7211L9.29922 15.7134L9.29585 15.7101L9.29432 15.7085L9.2936 15.7078C9.29324 15.7075 9.2929 15.7071 9.99207 15.0079L9.2936 15.7078C8.90307 15.3173 8.90237 14.6834 9.29289 14.2929C9.68007 13.9057 10.3057 13.9024 10.697 14.2829L10.7054 14.2904C10.7189 14.3022 10.7463 14.3253 10.7875 14.3562C10.87 14.4182 11.0067 14.5103 11.1972 14.6056C11.5749 14.7944 12.1728 15 13 15C13.6412 15 14.1519 14.6839 14.5429 14.2929C14.7357 14.1001 14.8825 13.9046 14.9804 13.7578C15.0288 13.6852 15.0637 13.6268 15.0849 13.5898C15.0954 13.5713 15.1024 13.5584 15.1059 13.5519L15.1081 13.5478C15.3566 13.0575 15.9549 12.8594 16.4472 13.1056Z" fill="currentColor"/>
                <path d="M14 9C13.4477 9 13 9.44771 13 10C13 10.5523 13.4477 11 14 11H16C16.5523 11 17 10.5523 17 10C17 9.44771 16.5523 9 16 9H14Z" fill="currentColor"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23ZM12 20.9932C7.03321 20.9932 3.00683 16.9668 3.00683 12C3.00683 7.03321 7.03321 3.00683 12 3.00683C16.9668 3.00683 20.9932 7.03321 20.9932 12C20.9932 16.9668 16.9668 20.9932 12 20.9932Z" fill="currentColor"/>
              </svg>
              스티커 선택
            </StickerSheetTitle>
            
            {(() => {
              const categories = ['전체', ...Array.from(new Set(stickerLibrary.map(s => s.category)))];
              const displayedStickers = selectedStickerCategory === '전체' 
                ? stickerLibrary 
                : stickerLibrary.filter(s => s.category === selectedStickerCategory);

              return (
                <>
                  {categories.length > 1 && (
                    <div className="no-scrollbar" style={{ display: 'flex', gap: '8px', padding: '0 24px 12px', overflowX: 'auto', borderBottom: '1px solid #EBE4D5', marginBottom: '12px' }}>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => setSelectedStickerCategory(cat)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            border: selectedStickerCategory === cat ? 'none' : '1px solid #EBE4D5',
                            backgroundColor: selectedStickerCategory === cat ? '#E07040' : '#FFF',
                            color: selectedStickerCategory === cat ? '#FFF' : '#888',
                            fontSize: '13px',
                            fontFamily: "'Jua', sans-serif",
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s',
                            flexShrink: 0
                          }}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}
                  <StickerGrid>
                    {stickerLibLoading ? (
                      <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '24px 0', color: '#B0A090', fontFamily: "'Jua', sans-serif", fontSize: '14px' }}>
                        로딩 중…
                      </div>
                    ) : displayedStickers.length === 0 ? (
                      <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '24px 0', color: '#B0A090', fontFamily: "'Jua', sans-serif", fontSize: '14px' }}>
                        해당 카테고리의 스티커가 없습니다
                      </div>
                    ) : (
                      displayedStickers.map(s => (
                        <StickerThumbWrap key={s.itemId}>
                          <StickerThumb onClick={() => addSticker(s)}>
                            <img src={s.imageUrl} alt={s.label} />
                          </StickerThumb>
                          <StickerThumbLabel>{s.label}</StickerThumbLabel>
                        </StickerThumbWrap>
                      ))
                    )}
                  </StickerGrid>
                </>
              );
            })()}
          </StickerSheet>
        </StickerPanelOverlay>

        {/* ── Result Modal ── */}
        {modalState && (
          <ModalOverlay>
            <ModalContent>
              <ModalTitle>
                {modalState.type === 'success' ? (isKorean ? '알림' : 'Notice') : (isKorean ? '오류' : 'Error')}
              </ModalTitle>
              <ModalDesc>{modalState.message}</ModalDesc>
              <ModalBtn onClick={() => {
                if (modalState.type === 'success') window.location.href = '/letterbox?tab=transit';
                else setModalState(null);
              }}>
                {isKorean ? '확인' : 'OK'}
              </ModalBtn>
            </ModalContent>
          </ModalOverlay>
        )}
      </ResponsiveShell>
    </>
  );
};

export default Letter;
